import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { TabsContent } from '../../components/ui/tabs';
import { getWarrantyError, hasWarrantyErrors } from '../../utils/validateWarranty';
import { NumericInput } from '../../components/NumericInput';
import { TruncatedValue } from '../shared/TruncatedValue';
import { Plus, Trash2, Edit2, Upload, ClipboardPaste, Loader2, X, Image as ImageIcon } from 'lucide-react';
import ColorPickerPopover from '../../components/ColorPickerPopover';

export const SFTypesSection = ({
  // Data
  storefrontTypes,
  selectedStorefrontTypeIds,
  newSFType,
  editingSFType,
  showSFTypeDialog,
  sfTypePictureUrl,
  pendingSFTypePicture,
  sfTypePictureUploading,
  operationLoading,
  // Permissions
  canManageProject,
  // Handlers
  setNewSFType,
  setShowSFTypeDialog,
  setEditingSFType,
  setSFTypePictureUrl,
  setPendingSFTypePicture,
  setStorefrontTypes,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateSFType,
  handleEditSFType,
  handleDeleteSFType,
  handleSFDrawingUpload,
  handleSFTypePasteFromClipboard,
  handleDeleteSFDrawing,
  handleOpenSFDrawingView,
  // API
  API,
  axios,
  toast,
}) => {
  return (
    <TabsContent value="sf-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Storefront Types</h2>
        <div className="flex gap-2">
          {canManageProject && storefrontTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={() => selectAllItems('storefrontType')}
              >
                {selectedStorefrontTypeIds.length === storefrontTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('storefrontType')}
                disabled={selectedStorefrontTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedStorefrontTypeIds.length})
              </Button>
            </>
          )}
          {canManageProject && (
            <Dialog open={showSFTypeDialog} onOpenChange={(open) => {
              if (open && !editingSFType) {
                setSFTypePictureUrl(null);
                if (pendingSFTypePicture?.previewUrl) {
                  URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
                }
                setPendingSFTypePicture(null);
                setNewSFType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  material: '',
                  finishType: '',
                  interiorFinishColor: '',
                  exteriorFinishColor: '',
                  productWarrantyYears: null,
                  hardwareWarrantyYears: null,
                  finishWarrantyYears: null,
                  installationWarrantyYears: null,
                  markerColor: '#39FF14'
                });
              }
              setShowSFTypeDialog(open);
              if (!open) {
                setEditingSFType(null);
                setSFTypePictureUrl(null);
                if (pendingSFTypePicture?.previewUrl) {
                  URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
                }
                setPendingSFTypePicture(null);
                setNewSFType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  material: '',
                  finishType: '',
                  interiorFinishColor: '',
                  exteriorFinishColor: '',
                  productWarrantyYears: null,
                  hardwareWarrantyYears: null,
                  finishWarrantyYears: null,
                  installationWarrantyYears: null,
                  markerColor: '#39FF14'
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add SF Type
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSFType ? 'Edit Storefront Type' : 'Create Storefront Type'}</DialogTitle>
                <DialogDescription>Define a storefront system type</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSFType} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., SF1, SF2) *</Label>
                    <Input value={newSFType.name} onChange={(e) => setNewSFType({...newSFType, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newSFType.manufacturer} onChange={(e) => setNewSFType({...newSFType, manufacturer: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newSFType.model} onChange={(e) => setNewSFType({...newSFType, model: e.target.value})} required />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Material *</Label>
                  <div className="flex gap-6">
                    {['Aluminum', 'Steel', 'Wood'].map((mat) => (
                      <div key={mat} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id={`sf-material-${mat}`}
                          name="sf-material"
                          checked={newSFType.material === mat}
                          onChange={() => setNewSFType({...newSFType, material: mat})}
                          className="w-4 h-4 text-orange-600 cursor-pointer"
                        />
                        <label htmlFor={`sf-material-${mat}`} className="text-sm font-medium cursor-pointer">{mat}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Finish Type *</Label>
                    <Input value={newSFType.finishType} onChange={(e) => setNewSFType({...newSFType, finishType: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Interior Finish Color *</Label>
                    <Input value={newSFType.interiorFinishColor} onChange={(e) => setNewSFType({...newSFType, interiorFinishColor: e.target.value})} placeholder="e.g., White, RAL 9016" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Exterior Finish Color *</Label>
                    <Input value={newSFType.exteriorFinishColor} onChange={(e) => setNewSFType({...newSFType, exteriorFinishColor: e.target.value})} placeholder="e.g., Bronze, RAL 8001" required />
                  </div>
                </div>
                
                {/* Appearance */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm border-b pb-2">Appearance</h4>
                  <div className="space-y-2">
                    <Label>Marker Color</Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={newSFType.markerColor || '#39FF14'}
                        onChange={(e) => setNewSFType({...newSFType, markerColor: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                        title="Choose marker color for elevations"
                        data-testid="sf-type-marker-color-input"
                      />
                      <span className="text-sm text-slate-500">Used for this type's markers on elevations</span>
                    </div>
                  </div>
                </div>
                
                {/* Picture Upload */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm border-b pb-2">SF Type Picture</h4>
                  {editingSFType && (sfTypePictureUrl || editingSFType?.drawingCloudinary?.urls?.medium || editingSFType?.drawingUrl) && (
                    <div className="mb-4">
                      <img 
                        src={sfTypePictureUrl || editingSFType?.drawingCloudinary?.urls?.medium || `${API}${editingSFType?.drawingUrl}`} 
                        alt="SF type drawing" 
                        className="max-w-full h-auto max-h-64 border rounded"
                      />
                    </div>
                  )}
                  {!editingSFType && pendingSFTypePicture?.previewUrl && (
                    <div className="mb-4">
                      <div className="relative inline-block">
                        <img 
                          src={pendingSFTypePicture.previewUrl} 
                          alt="Pending SF type drawing" 
                          className="max-w-full h-auto max-h-64 border rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
                            setPendingSFTypePicture(null);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-green-600 mt-2">✓ Picture ready - will be uploaded when SF type is created</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Upload Picture (PNG/JPEG/PDF)</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleSFTypePasteFromClipboard(editingSFType?.id || null)}
                        disabled={sfTypePictureUploading === editingSFType?.id}
                      >
                        {sfTypePictureUploading === editingSFType?.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ClipboardPaste className="h-4 w-4 mr-2" />
                        )}
                        Paste
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('sf-type-drawing-modal').click()}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {(sfTypePictureUrl || editingSFType?.drawingCloudinary?.urls?.medium || editingSFType?.drawingUrl || pendingSFTypePicture) ? 'Change Picture' : 'Upload Picture'}
                      </Button>
                      <input
                        id="sf-type-drawing-modal"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,application/pdf"
                        onChange={(e) => {
                          if (editingSFType) {
                            handleSFDrawingUpload(e, editingSFType.id);
                          } else {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (pendingSFTypePicture?.previewUrl) {
                                URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
                              }
                              const previewUrl = URL.createObjectURL(file);
                              setPendingSFTypePicture({ file, previewUrl });
                            }
                          }
                          e.target.value = '';
                        }}
                        style={{ display: 'none' }}
                      />
                    </div>
                    {!editingSFType && !pendingSFTypePicture && (
                      <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
                    )}
                  </div>
                </div>
                
                {/* Warranty Periods Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Warranty Periods (Years)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Product Warranty (Years)</Label>
                      <NumericInput value={newSFType.productWarrantyYears || ''} onChange={(v) => setNewSFType({...newSFType, productWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 10" className={getWarrantyError(newSFType.productWarrantyYears) ? 'border-red-500' : ''} data-testid="sf-product-warranty-input" />
                      {getWarrantyError(newSFType.productWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newSFType.productWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Hardware Warranty (Years)</Label>
                      <NumericInput value={newSFType.hardwareWarrantyYears || ''} onChange={(v) => setNewSFType({...newSFType, hardwareWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 5" className={getWarrantyError(newSFType.hardwareWarrantyYears) ? 'border-red-500' : ''} data-testid="sf-hardware-warranty-input" />
                      {getWarrantyError(newSFType.hardwareWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newSFType.hardwareWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Finish Warranty (Years)</Label>
                      <NumericInput value={newSFType.finishWarrantyYears || ''} onChange={(v) => setNewSFType({...newSFType, finishWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 15" className={getWarrantyError(newSFType.finishWarrantyYears) ? 'border-red-500' : ''} data-testid="sf-finish-warranty-input" />
                      {getWarrantyError(newSFType.finishWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newSFType.finishWarrantyYears)}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label>Installation Warranty (Years)</Label>
                      <NumericInput value={newSFType.installationWarrantyYears || ''} onChange={(v) => setNewSFType({...newSFType, installationWarrantyYears: v ? parseInt(v) : null})} placeholder="e.g., 2" className={getWarrantyError(newSFType.installationWarrantyYears) ? 'border-red-500' : ''} data-testid="sf-installation-warranty-input" />
                      {getWarrantyError(newSFType.installationWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newSFType.installationWarrantyYears)}</p>}
                    </div>
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={operationLoading || hasWarrantyErrors([newSFType.productWarrantyYears, newSFType.hardwareWarrantyYears, newSFType.finishWarrantyYears, newSFType.installationWarrantyYears])}>
                  {operationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingSFType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingSFType ? 'Update SF Type' : 'Create SF Type'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {storefrontTypes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-slate-600">No storefront types created yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {storefrontTypes.map((sf, index) => (
            <Card key={sf.id} className={selectedStorefrontTypeIds.includes(sf.id) ? 'ring-2 ring-blue-500' : ''}>
              <div className="flex">
                {/* Checkbox column */}
                {canManageProject && (
                  <div className="w-12 flex-shrink-0 flex items-start justify-center pt-4 border-r">
                    <Checkbox
                      checked={selectedStorefrontTypeIds.includes(sf.id)}
                      onCheckedChange={(checked) => toggleItemSelection(sf.id, 'storefrontType', { target: { checked } })}
                      className="h-5 w-5"
                    />
                  </div>
                )}
                
                {/* Picture column - Left */}
                <div className="flex-shrink-0 p-4 border-r" style={{ width: '250px', height: '300px' }}>
                  {(sf.drawingCloudinary?.urls?.medium || sf.drawingUrl) ? (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        <div className="flex gap-1">
                          {canManageProject && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSFTypePasteFromClipboard(sf.id)}
                                title="Paste image from clipboard"
                                disabled={sfTypePictureUploading === sf.id}
                              >
                                {sfTypePictureUploading === sf.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ClipboardPaste className="h-3 w-3" />
                                )}
                              </Button>
                              <label className="cursor-pointer">
                                <Button variant="outline" size="sm" asChild>
                                  <span><Upload className="h-3 w-3" /></span>
                                </Button>
                                <input
                                  type="file"
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png,.dwg"
                                  onChange={(e) => handleSFDrawingUpload(e, sf.id)}
                                />
                              </label>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteSFDrawing(sf.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      <div
                        className="flex-1 cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-center overflow-hidden"
                        onClick={() => handleOpenSFDrawingView(sf)}
                      >
                        <img
                          src={sf.drawingCloudinary?.urls?.large || `${API}${sf.drawingUrl}?t=${sf.updatedAt || sf.createdAt || 'default'}`}
                          alt="Storefront Type Picture"
                          className="max-w-full max-h-full object-contain border rounded"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex flex-col">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-sm">Picture</h4>
                        {canManageProject && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSFTypePasteFromClipboard(sf.id)}
                              title="Paste image from clipboard"
                              disabled={sfTypePictureUploading === sf.id}
                            >
                              {sfTypePictureUploading === sf.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ClipboardPaste className="h-3 w-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <Button variant="outline" size="sm" asChild>
                                <span><Upload className="h-3 w-3" /></span>
                              </Button>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png,.dwg"
                                onChange={(e) => handleSFDrawingUpload(e, sf.id)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        {canManageProject ? (
                          <label className="cursor-pointer w-full">
                            <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-slate-50 transition-colors">
                              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                              <p className="text-sm text-slate-600">Upload Picture</p>
                              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.dwg"
                              onChange={(e) => handleSFDrawingUpload(e, sf.id)}
                            />
                          </label>
                        ) : (
                          <div className="text-center text-slate-400">
                            <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                            <p className="text-sm">No Picture</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Content column - Right */}
                <div className="flex-1 p-4 flex flex-col justify-between" style={{ height: '300px' }}>
                  {/* Action buttons */}
                  {canManageProject && (
                    <div className="flex gap-2 items-center justify-end mb-2">
                      <ColorPickerPopover
                        id={`sf-${sf.id}`}
                        currentColor={sf.markerColor}
                        pendingColor={sf.pendingMarkerColor}
                        isOpen={sf.colorPickerOpen}
                        isSaving={sf.savingColor}
                        onOpenChange={(open) => {
                          setStorefrontTypes(prev => prev.map(t => 
                            t.id === sf.id ? { ...t, colorPickerOpen: open, pendingMarkerColor: open ? t.pendingMarkerColor : undefined } : t
                          ));
                        }}
                        onColorChange={(newColor) => {
                          setStorefrontTypes(prev => prev.map(t => 
                            t.id === sf.id ? { ...t, pendingMarkerColor: newColor } : t
                          ));
                        }}
                        onSave={async () => {
                          setStorefrontTypes(prev => prev.map(t => 
                            t.id === sf.id ? { ...t, savingColor: true } : t
                          ));
                          try {
                            await axios.put(`${API}/storefront-types/${sf.id}`, {
                              ...sf,
                              markerColor: sf.pendingMarkerColor
                            });
                            setStorefrontTypes(prev => prev.map(t => 
                              t.id === sf.id ? { ...t, markerColor: sf.pendingMarkerColor, pendingMarkerColor: undefined, savingColor: false, colorPickerOpen: false } : t
                            ));
                            toast.success('Marker color saved');
                          } catch (error) {
                            setStorefrontTypes(prev => prev.map(t => 
                              t.id === sf.id ? { ...t, savingColor: false } : t
                            ));
                            toast.error('Failed to update marker color');
                          }
                        }}
                      />
                      <Button variant="outline" size="sm" onClick={() => handleEditSFType(sf)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteSFType(sf.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Rows of content */}
                  <div className="flex-1 flex flex-col justify-evenly text-sm">
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Type</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.name} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Manufacturer</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.manufacturer} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Model</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.model} className="flex-1" />
                    </div>
                    
                    <div className="flex border-b pb-2">
                      <div className="font-semibold w-[95px]">Material</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.material} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Finish Type</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.finishType} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Interior Color</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.interiorFinishColor} className="w-[116px]" />
                      <div className="font-semibold w-[95px]">Exterior Color</div>
                      <div className="w-[10px]">:</div>
                      <TruncatedValue value={sf.exteriorFinishColor} className="flex-1" />
                    </div>
                    
                    <div className="flex">
                      <div className="font-semibold w-[95px]">Product Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{sf.productWarrantyYears ? `${sf.productWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Hardware Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{sf.hardwareWarrantyYears ? `${sf.hardwareWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Finish Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="w-[116px]">{sf.finishWarrantyYears ? `${sf.finishWarrantyYears} Years` : 'N/A'}</div>
                      <div className="font-semibold w-[95px]">Install Warranty</div>
                      <div className="w-[10px]">:</div>
                      <div className="flex-1">{sf.installationWarrantyYears ? `${sf.installationWarrantyYears} Years` : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </TabsContent>
  );
};

export default SFTypesSection;
